<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  notifications: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['dismiss'])

const countdowns = ref(new Map())
const intervals = ref(new Map())

function dismissNotification(id) {
  // Clear any active countdown for this notification
  if (intervals.value.has(id)) {
    clearInterval(intervals.value.get(id))
    intervals.value.delete(id)
  }
  countdowns.value.delete(id)
  emit('dismiss', id)
}

function startCountdown(notification) {
  if (!notification.autoDismiss || countdowns.value.has(notification.id)) return
  
  const duration = notification.duration || 3000 // Default to 3 seconds
  const startTime = Date.now()
  countdowns.value.set(notification.id, { remaining: duration, total: duration })
  
  const interval = setInterval(() => {
    const elapsed = Date.now() - startTime
    const remaining = Math.max(0, duration - elapsed)
    
    countdowns.value.set(notification.id, { 
      remaining, 
      total: duration,
      percentage: (remaining / duration) * 100
    })
    
    if (remaining <= 0) {
      clearInterval(interval)
      intervals.value.delete(notification.id)
      countdowns.value.delete(notification.id)
      dismissNotification(notification.id)
    }
  }, 50) // Update every 50ms for smooth animation
  
  intervals.value.set(notification.id, interval)
}

// Stack notifications vertically with proper spacing and right positioning
const processedNotifications = computed(() => {
  return props.notifications.map((notification, index) => {
    // Start countdown for new notifications
    if (notification.autoDismiss !== false && !countdowns.value.has(notification.id)) {
      setTimeout(() => startCountdown(notification), 100)
    }
    
    return {
      ...notification,
      style: {
        top: `${24 + (index * 80)}px` // Stack notifications 80px apart
      },
      countdown: countdowns.value.get(notification.id)
    }
  })
})

onUnmounted(() => {
  // Clear all intervals when component is unmounted
  intervals.value.forEach(interval => clearInterval(interval))
  intervals.value.clear()
  countdowns.value.clear()
})
</script>

<template>
  <div class="notification-system">
    <TransitionGroup
      name="notification"
      tag="div"
      class="fixed top-6 z-50 pointer-events-none"
      style="right: 24px; width: 400px;"
    >
      <div
        v-for="notification in processedNotifications"
        :key="notification.id"
        :style="notification.style"
        class="absolute pointer-events-auto w-full"
      >
        <!-- Success Notification -->
        <div 
          v-if="notification.type === 'success'"
          class="bg-white/80 border-l-4 border-green-500 text-gray-800 px-7 py-5 rounded-xl shadow-2xl backdrop-blur-lg hover:bg-white/90 transition-all duration-300 flex items-center min-w-[350px] max-w-md relative overflow-hidden"
          style="box-shadow: 0 8px 32px 0 rgba(60, 180, 80, 0.10);"
        >
          <!-- Countdown Progress Bar -->
          <div 
            v-if="notification.countdown && notification.autoDismiss !== false"
            class="absolute bottom-0 left-0 h-1 bg-green-400 transition-all duration-75 ease-linear"
            :style="{ width: `${notification.countdown.percentage || 0}%` }"
          ></div>
          
          <div class="flex-shrink-0 mr-4">
            <div class="w-10 h-10 rounded-full bg-green-100/70 flex items-center justify-center backdrop-blur relative">
              <i class="fas fa-check text-green-500 text-2xl"></i>
              <!-- Countdown circle -->
              <div 
                v-if="notification.countdown && notification.autoDismiss !== false"
                class="absolute inset-0 rounded-full border-2 border-green-300"
                :style="{
                  background: `conic-gradient(from 0deg, transparent ${360 - (notification.countdown.percentage / 100 * 360)}deg, rgba(34, 197, 94, 0.3) ${360 - (notification.countdown.percentage / 100 * 360)}deg)`
                }"
              ></div>
            </div>
          </div>
          <div class="flex-1">
            <p class="font-semibold text-gray-900 text-base leading-tight mb-0.5">
              {{ notification.title || 'Success!' }}
            </p>
            <p class="text-[15px] text-gray-600/90 leading-snug">
              {{ notification.message }}
            </p>
          </div>
          <button 
            @click="dismissNotification(notification.id)"
            class="ml-4 text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100/60"
            aria-label="Close"
          >
            <i class="fas fa-times text-lg"></i>
          </button>
        </div>

        <!-- Error Notification -->
        <div 
          v-else-if="notification.type === 'error'"
          class="bg-white/80 border-l-4 border-red-500 text-gray-800 px-7 py-5 rounded-xl shadow-2xl backdrop-blur-lg hover:bg-white/90 transition-all duration-300 flex items-center min-w-[350px] max-w-md relative overflow-hidden"
          style="box-shadow: 0 8px 32px 0 rgba(220, 38, 38, 0.10);"
        >
          <!-- Countdown Progress Bar -->
          <div 
            v-if="notification.countdown && notification.autoDismiss !== false"
            class="absolute bottom-0 left-0 h-1 bg-red-400 transition-all duration-75 ease-linear"
            :style="{ width: `${notification.countdown.percentage || 0}%` }"
          ></div>
          
          <div class="flex-shrink-0 mr-4">
            <div class="w-10 h-10 rounded-full bg-red-100/70 flex items-center justify-center backdrop-blur relative">
              <i class="fas fa-exclamation-circle text-red-500 text-2xl"></i>
              <!-- Countdown circle -->
              <div 
                v-if="notification.countdown && notification.autoDismiss !== false"
                class="absolute inset-0 rounded-full border-2 border-red-300"
                :style="{
                  background: `conic-gradient(from 0deg, transparent ${360 - (notification.countdown.percentage / 100 * 360)}deg, rgba(239, 68, 68, 0.3) ${360 - (notification.countdown.percentage / 100 * 360)}deg)`
                }"
              ></div>
            </div>
          </div>
          <div class="flex-1">
            <p class="font-semibold text-gray-900 text-base leading-tight mb-0.5">
              {{ notification.title || 'Error!' }}
            </p>
            <p class="text-[15px] text-gray-600/90 leading-snug">
              {{ notification.message }}
            </p>
          </div>
          <button 
            @click="dismissNotification(notification.id)"
            class="ml-4 text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100/60"
            aria-label="Close"
          >
            <i class="fas fa-times text-lg"></i>
          </button>
        </div>

        <!-- Warning Notification -->
        <div 
          v-else-if="notification.type === 'warning'"
          class="bg-white/80 border-l-4 border-yellow-500 text-gray-800 px-7 py-5 rounded-xl shadow-2xl backdrop-blur-lg hover:bg-white/90 transition-all duration-300 flex items-center min-w-[350px] max-w-md relative overflow-hidden"
          style="box-shadow: 0 8px 32px 0 rgba(245, 158, 11, 0.10);"
        >
          <!-- Countdown Progress Bar -->
          <div 
            v-if="notification.countdown && notification.autoDismiss !== false"
            class="absolute bottom-0 left-0 h-1 bg-yellow-400 transition-all duration-75 ease-linear"
            :style="{ width: `${notification.countdown.percentage || 0}%` }"
          ></div>
          
          <div class="flex-shrink-0 mr-4">
            <div class="w-10 h-10 rounded-full bg-yellow-100/70 flex items-center justify-center backdrop-blur relative">
              <i class="fas fa-exclamation-triangle text-yellow-500 text-2xl"></i>
              <!-- Countdown circle -->
              <div 
                v-if="notification.countdown && notification.autoDismiss !== false"
                class="absolute inset-0 rounded-full border-2 border-yellow-300"
                :style="{
                  background: `conic-gradient(from 0deg, transparent ${360 - (notification.countdown.percentage / 100 * 360)}deg, rgba(245, 158, 11, 0.3) ${360 - (notification.countdown.percentage / 100 * 360)}deg)`
                }"
              ></div>
            </div>
          </div>
          <div class="flex-1">
            <p class="font-semibold text-gray-900 text-base leading-tight mb-0.5">
              {{ notification.title || 'Warning!' }}
            </p>
            <p class="text-[15px] text-gray-600/90 leading-snug">
              {{ notification.message }}
            </p>
          </div>
          <button 
            @click="dismissNotification(notification.id)"
            class="ml-4 text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100/60"
            aria-label="Close"
          >
            <i class="fas fa-times text-lg"></i>
          </button>
        </div>

        <!-- Info Notification -->
        <div 
          v-else-if="notification.type === 'info'"
          class="bg-white/80 border-l-4 border-blue-500 text-gray-800 px-7 py-5 rounded-xl shadow-2xl backdrop-blur-lg hover:bg-white/90 transition-all duration-300 flex items-center min-w-[350px] max-w-md relative overflow-hidden"
          style="box-shadow: 0 8px 32px 0 rgba(59, 130, 246, 0.10);"
        >
          <!-- Countdown Progress Bar -->
          <div 
            v-if="notification.countdown && notification.autoDismiss !== false"
            class="absolute bottom-0 left-0 h-1 bg-blue-400 transition-all duration-75 ease-linear"
            :style="{ width: `${notification.countdown.percentage || 0}%` }"
          ></div>
          
          <div class="flex-shrink-0 mr-4">
            <div class="w-10 h-10 rounded-full bg-blue-100/70 flex items-center justify-center backdrop-blur relative">
              <i class="fas fa-info-circle text-blue-500 text-2xl"></i>
              <!-- Countdown circle -->
              <div 
                v-if="notification.countdown && notification.autoDismiss !== false"
                class="absolute inset-0 rounded-full border-2 border-blue-300"
                :style="{
                  background: `conic-gradient(from 0deg, transparent ${360 - (notification.countdown.percentage / 100 * 360)}deg, rgba(59, 130, 246, 0.3) ${360 - (notification.countdown.percentage / 100 * 360)}deg)`
                }"
              ></div>
            </div>
          </div>
          <div class="flex-1">
            <p class="font-semibold text-gray-900 text-base leading-tight mb-0.5">
              {{ notification.title || 'Info!' }}
            </p>
            <p class="text-[15px] text-gray-600/90 leading-snug">
              {{ notification.message }}
            </p>
          </div>
          <button 
            @click="dismissNotification(notification.id)"
            class="ml-4 text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100/60"
            aria-label="Close"
          >
            <i class="fas fa-times text-lg"></i>
          </button>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.notification-enter-active {
  transition: all 0.3s ease-out;
}

.notification-leave-active {
  transition: all 0.2s ease-in;
}

.notification-enter-from {
  transform: translateX(calc(100% + 24px));
  opacity: 0;
}

.notification-enter-to {
  transform: translateX(0);
  opacity: 1;
}

.notification-leave-from {
  transform: translateX(0);
  opacity: 1;
}

.notification-leave-to {
  transform: translateX(calc(100% + 24px));
  opacity: 0;
}

.notification-move {
  transition: transform 0.3s ease;
}
</style> 